
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, PlayCircle, Video, Loader } from "lucide-react";
import Layout from "../components/Layout";
import Header from "../components/Header";
import PageTransition from "../components/PageTransition";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Checkbox } from "@/components/ui/checkbox";
import { User } from "lucide-react";
import ScriptEditor from "@/components/ScriptEditor";
import SimpleImageGallery from "@/components/SimpleImageGallery";
import { useSelector } from "react-redux";
import { RootState } from "@/store";
import { useMutation } from "@apollo/client";
import { EDIT_VIDEO } from "@/integrations/apollo/operations";
import { useAudioPlayback } from "@/hooks/useAudioPlayback";

interface DraftState {
  script: string;
  images: any[];
  // Don't add id here as it's not part of the state type
}

const EditVideo = () => {
  const { id } = useParams<{ id: string }>();
  const [images, setImages] = useState([]);
  const [reviewConfirmed, setReviewConfirmed] = useState(false);
  const [generatingVideo, setGeneratingVideo] = useState(false);
  const [draft, setDraft] = useState<DraftState>({ script: '', images: [] });
  const video = useSelector((state: RootState) => state.dashboardVideos.videos.find(v => v.id === id));
  const [editVideo] = useMutation(EDIT_VIDEO);
  const navigate = useNavigate();

  // Use the audio playback hook
  const {
    isPlaying,
    generatingAudio,
    audioRef,
    generateAudio,
    handleAudioEnded,
    resetAudio,
    audioUrl,
  } = useAudioPlayback();

  useEffect(() => {
    if (video?.edit_attempts_used !== 0) {
      toast.error("You have reached the maximum number of edit attempts.");
      navigate('/dashboard');
    }
  }, [video]);

  useEffect(() => {
    if (video) {
      setDraft({
        script: video.script || '',
        images: video.original_scraped_image_urls || [],
      });

      setImages(() => {
        return (video.original_scraped_image_urls || []).map((image, index) => ({
          id: index.toString(),
          url: image,
          selected: false,
        }));
      });
    }
  }, [video]);

  const handleScriptChange = (script: string) => {
    setDraft({ ...draft, script });
    resetAudio();
  };

  const handleImagesChange = (updatedImages) => {
    setImages(updatedImages);
  };

  const handleGenerateVideo = async () => {
    if (!reviewConfirmed) {
      toast.error("Please confirm you've reviewed all changes before generating");
      return;
    }

    const selectedImagesCount = images.filter(img => img.selected).length;
    if (selectedImagesCount !== 9) {
      toast.error(`Please select 9 images (you have selected ${selectedImagesCount})`);
      return;
    }

    setGeneratingVideo(true);

    try {
      const { data, errors } = await editVideo({
        variables: {
          video_id: id,
          script: draft.script,
          image_urls: images.filter(img => img.selected).map(img => img.url)
        }
      });

      if (errors) {
        toast.error('Failed to edit video');
        setGeneratingVideo(false);
        return;
      }

      toast.success('Video edited successfully');

      setGeneratingVideo(false);
      navigate('/dashboard');
    } catch (error) {
      console.error("Error while editing a video:", error);
      toast.error('Failed to edit video. Please try again later or contact our support team.');

      setGeneratingVideo(false);
    }
  };

  const handleAudioPreview = async () => {
    // Check if video and avatar exist and have the proper properties
    if (!video?.avatar?.default_voice?.id || !draft.script) {
      toast.error("Script or actor voice not available");
      return;
    }

    await generateAudio(draft.script, video.avatar.default_voice?.id);
  };

  const ReviewConfirmation = () => (
    <div className="flex items-start space-x-3 py-3">
      <Checkbox
        id="review-confirmation"
        checked={reviewConfirmed}
        onCheckedChange={(checked) => setReviewConfirmed(checked as boolean)}
        className="mt-1"
      />
      <div className="space-y-1">
        <label
          htmlFor="review-confirmation"
          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
        >
          I have reviewed my script and images
        </label>
        <p className="text-xs text-muted-foreground">
          Please confirm you've reviewed all changes before generating your video
        </p>
      </div>
    </div>
  );

  if (!video) return null;

  // Safely create voiceName for audio preview
  const voiceName = video.avatar?.name || 'Unknown';

  return (
    <Layout>
      <PageTransition>
        <Header />

        <motion.div
          className="space-y-6 max-w-7xl mx-auto px-4 pb-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <div className="flex items-center gap-2 mb-2">
            <Link to="/dashboard" className="text-muted-foreground hover:text-foreground flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              <h2 className="text-xl font-bold tracking-tight">Dashboard</h2>
            </Link>
          </div>

          <div className="text-center mb-6">
            <h2 className="font-bold tracking-tight mb-3 text-xl">Edit Your Video</h2>
            <p className="text-muted-foreground text-balance">
              Customize your script and select images for your property video.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <ScriptEditor
                script={draft.script}
                onScriptChange={handleScriptChange}
              />

              <div className="mt-4">
                <h3 className="text-sm font-medium mb-2 text-muted-foreground">Preview with your selected actor</h3>
                <section className="border rounded-xl p-2">
                  <div className="flex items-center space-x-3 flex-shrink-0">
                    <Avatar className="h-10 w-10 border-2 border-primary/10">
                      <AvatarImage
                        src={video.avatar?.preview_image_url || '/placeholder.svg'}
                        alt={video.avatar?.name || 'Unknown Actor'}
                      />
                      <AvatarFallback className="bg-primary/5">
                        <User className="h-4 w-4 text-primary/70" />
                      </AvatarFallback>
                    </Avatar>

                    <div>
                      <p className="text-primary text-sm font-medium">{video.avatar?.name || 'Unknown Actor'}</p>
                      <p className="text-muted-foreground text-xs">{video.avatar?.description || ''}</p>
                    </div>
                  </div>

                  <hr className="my-2" />

                  <div className="">
                    { audioUrl && (
                      <audio ref={audioRef} controls onEnded={handleAudioEnded} className="w-full" />
                    )}

                    { !audioUrl && (
                      <Button
                        variant="outline"
                        size="lg"
                        onClick={handleAudioPreview}
                        disabled={generatingAudio || !draft.script || !video?.avatar?.default_heygen_voice_id}
                        className="w-full"
                      >
                        {generatingAudio ? (
                          <>
                            <Loader className="h-4 w-4 animate-spin" />
                            Generating Audio...
                          </>
                        ) : (
                          <>
                            <PlayCircle className="h-4 w-4" />
                            Preview Script in Actor's Voice
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </section>
              </div>
            </div>
            <SimpleImageGallery
              images={images}
              onImagesChange={handleImagesChange}
              maxSelected={9}
            />
          </div>

          <div className="glass rounded-xl p-4 mt-6">
            <h3 className="font-medium mb-3">Tips for a great video</h3>
            <ul className="space-y-3 text-sm">
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Keep your script concise - under 150 words works best</span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Select the nicest property images to give a good first impression </span>
              </li>
              <li className="flex">
                <div className="mr-3 text-primary">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M16 12H8" />
                    <circle cx="12" cy="12" r="10" />
                  </svg>
                </div>
                <span>Preview the audio to ensure perfect pronunciation</span>
              </li>
            </ul>
          </div>

          <div className="glass rounded-xl p-4 mt-6 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex flex-col gap-2">
              <ReviewConfirmation />
            </div>

            <Button
              onClick={handleGenerateVideo}
              disabled={!reviewConfirmed || generatingVideo || images.filter(img => img.selected).length !== 9}
              className="flex items-center gap-2"
              size="lg"
            >
              <Video className="h-4 w-4" />
              {generatingVideo ? 'Generating...' : 'Generate Video'}
            </Button>
          </div>
        </motion.div>
      </PageTransition>
    </Layout>
  );
};

export default EditVideo;
